import organizationApi from '@/api/organization';
import * as Sentry from '@sentry/vue';
import { ROLES, SSO_SYNC_STATUS } from '@/utils';
import superAdminApi from '@/api/super-admin';

let userDetailRequestSequence = 0;

export default {
  getOrganizationMetaData({ commit }, payload) {
    return new Promise((resolve, reject) => {
      commit('setLoading', true);

      organizationApi
        .getOrganizationMetaData(payload)
        .then((response) => {
          const { data } = response || {};

          Sentry.configureScope(function (scope) {
            scope.setTag('auth-type', data.authenticationType);
            scope.setTag('org-name', data.organizationName);
          });

          commit('setOrganization', data);
          commit('setLoading', false);

          resolve(response);
        })
        .catch((err) => {
          reject(err);
          commit('setLoading', false);
        });
    });
  },
  getCourseProgress({ commit }, payload) {
    commit('setLoading', true);
    return new Promise((resolve, reject) => {
      organizationApi
        .getCourseProgress(payload)
        .then((res) => {
          commit('setLoading', false);
          commit('setCourses', res.data.courses);
          commit('setTotalUserCount', res.data.totalUserCount);
          resolve(res.data);
        })
        .catch((err) => {
          reject(err);
          commit('setLoading', false);
        });
    });
  },
  // Hotfix for 2.6.1 -  PERFCS-1356
  getOrganizationDetailForOfficerDownload({ commit }, payload) {
    return new Promise((resolve, reject) => {
      commit('setLoading', true);

      superAdminApi
        .getOrganizationDetail(payload)
        .then((response) => {
          const { data } = response || {};

          commit('setDistributionTypes', data);
          commit('setOrganizationName', data.name);
          commit('setLoading', false);

          resolve(response);
        })
        .catch((err) => {
          reject(err);
          commit('setLoading', false);
        });
    });
  },
  getUsersUsageData({ commit }, payload) {
    return new Promise((resolve, reject) => {
      commit('setLoading', true);
      organizationApi
        .getUsersUsageData(payload)
        .then((response) => {
          const { totalResources, officers, usageData } = response.data || {};

          commit('setUsageTotal', totalResources || officers.length);
          commit('setOfficerUsage', usageData);
          commit('setUsageOfficers', officers);
          commit('setLoading', false);
          resolve(response);
        })
        .catch((err) => {
          reject(err);
          commit('setLoading', false);
        });
    });
  },

  getDailyUsageData({ commit }, payload) {
    return new Promise((resolve, reject) => {
      commit('setLoading', true);

      organizationApi
        .getDailyUsageData(payload)
        .then((response) => {
          commit('setDailyUsage', response.data);
          commit('setLoading', false);
          resolve(response);
        })
        .catch((err) => {
          reject(err);
          commit('setLoading', false);
        });
    });
  },

  exportUsageData({ commit }, payload) {
    return new Promise((resolve, reject) => {
      commit('setLoading', true);

      organizationApi
        .exportUsageData(payload)
        .then((response) => {
          commit('setLoading', false);
          resolve(response);
        })
        .catch((err) => {
          reject(err);
          commit('setLoading', false);
        });
    });
  },

  getUserDetail({ commit }, payload) {
    const requestSequence = ++userDetailRequestSequence;
    return new Promise((resolve, reject) => {
      commit('setLoading', true);

      organizationApi
        .getUserDetail(payload)
        .then((response) => {
          if (requestSequence !== userDetailRequestSequence) {
            resolve(response.data);
            return;
          }
          const { user, assignedCourses, userDailyUsage } = response.data || {};
          commit('setUserDetailInfo', user);
          commit('setUserDetailAssignedCourses', assignedCourses);
          commit('setUserDetailDailyUsage', userDailyUsage);
          commit('setLoading', false);
          resolve(response.data);
        })
        .catch((err) => {
          reject(err);
          if (requestSequence === userDetailRequestSequence) {
            commit('setLoading', false);
          }
        });
    });
  },

  updateUserDetail({ commit }, payload) {
    return new Promise((resolve, reject) => {
      commit('setLoading', true);

      organizationApi
        .updateUserDetail(payload)
        .then((response) => {
          commit('setUserDetailInfo', response.data);
          commit('setLoading', false);
          resolve(response.data);
        })
        .catch((err) => {
          reject(err);
          commit('setLoading', false);
        });
    });
  },

  getAssignedCourses({ commit, state }, payload) {
    return new Promise((resolve, reject) => {
      commit('setLoading', true);
      // We have a special case where if the sortBy parameter
      // is 'name', we need to specify 'lastName' or 'firstName' instead
      // TODO: update column fields to constants
      if (payload.payload.sortBy && payload.payload.sortBy === 'name') {
        payload.payload.sortBy = state.organization
          .displayLastNameBeforeFirstName
          ? 'lastName'
          : 'firstName';
      }
      organizationApi
        .getAssignedUsersWithEligibleGroups(payload)
        .then((response) => {
          commit('setLoading', false);
          commit('setCourseAssignments', response.data);
          resolve(response);
        })
        .catch((err) => {
          reject(err);
          commit('setLoading', false);
        });
    });
  },

  getAllSupportRequests({ commit }, payload) {
    return new Promise((resolve, reject) => {
      commit('setLoading', true);

      organizationApi
        .getAllSupportRequests(payload)
        .then((response) => {
          const { data } = response || {};
          commit('setLoading', false);
          commit('setAllSupportRequests', data);
          resolve(data);
        })
        .catch((err) => {
          reject(err);
          commit('setLoading', false);
        });
    });
  },

  getSupportRequests({ commit }, payload) {
    return new Promise((resolve, reject) => {
      commit('setLoading', true);

      organizationApi
        .getSupportRequests(payload)
        .then((response) => {
          const { data } = response || {};
          commit('setLoading', false);
          commit('setSupportRequests', data);
          resolve(data);
        })
        .catch((err) => {
          reject(err);
          commit('setLoading', false);
        });
    });
  },

  assignCoursesBatch({ commit }, payload) {
    return new Promise((resolve, reject) => {
      commit('setLoading', true);

      organizationApi
        .assignCoursesBatch(payload)
        .then((response) => {
          commit('setLoading', false);
          resolve(response);
        })
        .catch((err) => {
          reject(err);
          commit('setLoading', false);
        });
    });
  },

  updateAssignedCourse({ commit }, payload) {
    return new Promise((resolve, reject) => {
      commit('setLoading', true);

      organizationApi
        .updateAssignedCourse(payload)
        .then((response) => {
          const { data } = response || {};
          commit('setAssignedCourseById', data);
          commit('setLoading', false);
          resolve(response);
        })
        .catch((err) => {
          reject(err);
          commit('setLoading', false);
        });
    });
  },

  async submitInPersonTraining({ commit }, args) {
    commit('setLoading', true);
    try {
      const response = await organizationApi.submitInPersonTraining(args);
      commit('setAssignedCourseById', response.data);
      return response.data;
    } finally {
      commit('setLoading', false);
    }
  },

  getUsers({ commit }, payload) {
    return new Promise((resolve, reject) => {
      commit('setLoading', true);

      organizationApi
        .getUsers(payload)
        .then((response) => {
          const { data } = response || {};
          commit('setUsers', data);
          commit('setLoading', false);
          resolve(response);
        })
        .catch((err) => {
          reject(err);
          commit('setLoading', false);
        });
    });
  },

  getOfficersInOrgDashboard({ commit }, payload) {
    return new Promise((resolve, reject) => {
      commit('setLoading', true);

      organizationApi
        .getOfficersInOrgDashboard(payload)
        .then((response) => {
          commit('setOfficersInOrgDashboard', response.data);
          commit('setLoading', false);
          resolve(response);
        })
        .catch((err) => {
          reject(err);
          commit('setLoading', false);
        });
    });
  },

  deleteSupportRequest({ commit }, payload) {
    return new Promise((resolve, reject) => {
      commit('setLoading', true);

      organizationApi
        .deleteSupportRequest(payload)
        .then((response) => {
          commit('setLoading', false);
          resolve(response);
        })
        .catch((err) => {
          reject(err);
          commit('setLoading', false);
        });
    });
  },

  deleteCourseAssignment({ commit }, payload) {
    return new Promise((resolve, reject) => {
      commit('setLoading', true);

      organizationApi
        .deleteCourseAssignment(payload)
        .then((response) => {
          commit('setLoading', false);
          resolve(response);
        })
        .catch((err) => {
          reject(err);
          commit('setLoading', false);
        });
    });
  },

  syncUsersFromOrganization(_context, payload) {
    return new Promise((resolve, reject) => {
      organizationApi
        .syncUsersFromOrganization(payload)
        .then((response) => {
          resolve(response);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  getSyncStatusForOrganization({ rootState, commit }, payload) {
    return new Promise((resolve, reject) => {
      organizationApi
        .getSyncStatusForOrganization(payload)
        .then((response) => {
          const { data } = response || {};
          const { syncStatus } = data || {};
          // we typically reset this state as null when start polling to differentiate with initial request
          // only if triggered with sso sync btn, we show summary modal
          // TODO: pass a flag via the payload to determine if the sync banner should display instead of using the vuex store
          if (rootState.user.showSsoSyncBanner === null) {
            commit(
              'user/setShowSsoSyncBanner',
              syncStatus &&
                syncStatus !== SSO_SYNC_STATUS.RUNNING &&
                syncStatus !== SSO_SYNC_STATUS.LAUNCHING
                ? true
                : null,
              { root: true }
            );
          }

          commit('setSsoSync', data);
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  getTrainingManagers({ commit }, payload) {
    return new Promise((resolve, reject) => {
      organizationApi
        .getTrainingManagers(payload)
        .then((response) => {
          commit('setTrainingOfficers', response.data.resources ?? []);
          resolve(response);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  validateUseOfForcePolicyUrl(_context, payload) {
    return new Promise((resolve, reject) => {
      organizationApi
        .validateUseOfForcePolicyUrl(payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  getPolicyByQuizId({ commit }, { orgId, courseId }) {
    commit('setLoading', true);
    return new Promise((resolve, reject) => {
      organizationApi
        .getOrgPolicyByCourseId({ orgId, courseId })
        .then((response) => {
          response = response.data;
          // If the policy has never been saved before add custom button to each question and mark first 4, 3, or 2 as visible,
          // otherwise check if the answer contains a custom button answer and if not, add it.
          if (!response.answerOverridesLastUpdated) {
            response.answerOverrides.forEach((a) => {
              for (let i = 0; i < a.buttons.length; i++) {
                a.buttons[i].isVisible = i < 4;
              }

              a.buttons.push({
                text: null,
                isVisible: false,
                isCustom: true,
                isCorrect: false,
              });
            });
          } else {
            response.answerOverrides.forEach((a) => {
              if (!a.buttons.filter((b) => b.isCustom).length) {
                a.buttons.push({
                  text: null,
                  isVisible: false,
                  isCustom: true,
                  isCorrect: false,
                });
              }
            });
          }
          commit('setCurrentQuizPolicy', response);
          commit('setLoading', false);
          resolve(response);
        })
        .catch((err) => {
          commit('setLoading', false);
          reject(err);
        });
    });
  },
  savePolicy({ commit }, { courseId, payload, orgId }) {
    // Swift through the custom buttons and remove any that have text value of null/empty //
    payload.answerOverrides.forEach((a) => {
      if (!a.buttons[a.buttons.length - 1].text) {
        a.buttons.pop();
      }
    });
    commit('setLoading', true);
    return new Promise((resolve, reject) => {
      organizationApi
        .saveOrgPolicy({ courseId, payload, orgId })
        .then(() => {
          commit('setLoading', false);
          resolve();
        })
        .catch((err) => {
          commit('setLoading', false);
          reject(err);
        });
    });
  },
  savePolicyDocumentLinks({ commit }, { courseUnifierId, payload, orgId }) {
    commit('setLoading', true);
    return new Promise((resolve, reject) => {
      organizationApi
        .savePolicyDocumentLinks({ courseUnifierId, payload, orgId })
        .then((res) => {
          commit('setLoading', false);
          resolve(res.data);
        })
        .catch((err) => {
          commit('setLoading', false);
          reject(err);
        });
    });
  },
  getCycles({ commit, rootState }, { orgId, onlyCyclesWithAssignments }) {
    return new Promise((resolve, reject) => {
      organizationApi
        .getCourseCycles(orgId, onlyCyclesWithAssignments)
        .then((res) => {
          const isSuperAdmin =
            rootState.user.currentUser?.roles?.some(
              (x) => x?.toLowerCase() === ROLES.SUPER_ADMIN.toLowerCase()
            ) || false;
          const activeOrgId = rootState.user.currentUser?.organizationId;

          if (isSuperAdmin && activeOrgId !== orgId) {
            resolve(res.data);
            return;
          }

          commit('setCycles', res.data);
          resolve(res.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
};
